import { manageOrdersUrl, ordersCRUDUrl, ordersPaymentCRUDUrl, orderSummaryUrl, paymentMethodsApiUrl, shopOrderItemCRUDUrl, shopOrderOfflineUrl, shopOrderReturnCancellationUrl, shopOrdersUrl, trackUserOrdersUrl } from "../constants/apis";
import axios from "axios";
import { getUserToken, getUserId, isSalesStaffRole } from "../helpers/local_storage_helpers";

/**
 * Shop order lifecycle — keep in sync with backend STATUS_CHOICES_ORDER.
 * Use for dropdowns, filters, and labels (value = API enum, label = UI text).
 */
export const STATUS_CHOICES_ORDER = [
    { value: "PENDING", label: "Pending" },
    { value: "CONFIRMED", label: "Confirmed" }, // admin/staff accepted the order
    { value: "IN_PACKAGING", label: "In Packaging" },
    { value: "DELIVERED", label: "Delivered" },
    { value: "DISPATCHED", label: "Dispatched" },
    { value: "CANCELLED", label: "Cancelled" },
    { value: "RETURNED", label: "Returned" },
];

/** Map status code → display label (fallback: the code itself). */
export const getOrderStatusLabel = (status) => {
    if (status == null || status === "") return "—";
    const found = STATUS_CHOICES_ORDER.find((o) => o.value === status);
    return found ? found.label : String(status);
};

/** For staff role, merge `user_id` into the query string so APIs scope to the logged-in user. */
const withStaffUserIdQuery = (query = "") => {
    if (!isSalesStaffRole()) return query;
    let id;
    try {
        id = getUserId();
    } catch {
        return query;
    }
    if (id == null || id === "") return query;
    const raw = String(query).replace(/^\?/, "");
    const params = new URLSearchParams(raw);
    // Customer lookup by phone — do not inject logged-in staff id
    if (params.has("mobile_number")) {
        return query || "";
    }
    if (!params.has("user_id")) {
        params.set("user_id", String(id));
    }
    const s = params.toString();
    return s ? `?${s}` : `?user_id=${encodeURIComponent(id)}`;
};

export const getOrders = async (query, additionalConfig = {}) => {
    const config = {
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Token ${getUserToken()}`,
        },
        ...additionalConfig,
    };

    const response = await axios.get(shopOrdersUrl + withStaffUserIdQuery(query || ""), config);

    return response;
};

/** Build query for track-user-orders: prefers user_id, else mobile_number (10 digits, no +91). */
export const buildTrackUserOrdersQuery = ({ userId, mobileNumber } = {}) => {
    if (userId != null && String(userId).trim() !== "") {
        return `?user_id=${encodeURIComponent(String(userId).trim())}`;
    }
    let digits = String(mobileNumber || "").replace(/\D/g, "");
    if (digits.length > 10 && digits.startsWith("91")) {
        digits = digits.slice(2);
    }
    digits = digits.slice(0, 10);
    if (digits) {
        return `?mobile_number=${encodeURIComponent(digits)}`;
    }
    return null;
};

export const getTrackUserOrders = async (query) => {
    const config = {
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Token ${getUserToken()}`,
        },
    };

    const raw = String(query || "").replace(/^\?/, "");
    const params = new URLSearchParams(raw);
    // track-user-orders: only auto-scope staff when no customer lookup param is provided
    const scopedQuery =
        params.has("mobile_number") || params.has("user_id")
            ? query || ""
            : withStaffUserIdQuery(query || "");

    const response = await axios.get(trackUserOrdersUrl + scopedQuery, config);

    return response;
};

export const getManageOrders = async (query) => {
    const config = {
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Token ${getUserToken()}`,
        },
    };

    const response = await axios.get(manageOrdersUrl + withStaffUserIdQuery(query || ""), config);

    return response;
};

export const getOrderDetails = async (id) => {
    const config = {
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Token ${getUserToken()}`,
        },
    };

    const response = await axios.get(shopOrdersUrl + withStaffUserIdQuery(`?order_id=${id || ""}`), config);

    return response;
};

export const getPaymentTypes = async (query) => {
    const config = {
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Token ${getUserToken()}`,
        },
    };

    const response = await axios.get(paymentMethodsApiUrl, config);

    return response;
};

export const getOrdersSummary = async (query = "", additionalConfig = {}) => {
    const config = {
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Token ${getUserToken()}`,
        },
        ...additionalConfig
    };

    const response = await axios.get(orderSummaryUrl + withStaffUserIdQuery(query || ""), config);
    return response;
};

export const postOrder = async (data) => {
    const config = {
        headers: {
            // "Content-Type": "application/json",
            "Authorization": `Token ${getUserToken()}`,
        },
    };
    const response = await axios.post(shopOrderOfflineUrl, data, config);
    return response;
};

export const patchOrder = async ({ data, id }) => {
    const config = {
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Token ${getUserToken()}`,
        },
    };
    const response = await axios.patch(ordersCRUDUrl + id + "/", data, config);
    return response;
};

export const patchOrderPayment = async ({ data, id }) => {
    const config = {
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Token ${getUserToken()}`,
        },
    };
    const response = await axios.patch(ordersPaymentCRUDUrl + id + "/", data, config);
    return response;
};

export const postOrderPayment = async ({ data }) => {
    const config = {
        headers: {
            // "Content-Type": "application/json",
            "Authorization": `Token ${getUserToken()}`,
        },
    };

    const response = await axios.post(ordersPaymentCRUDUrl, data, config);
    return response;
};

export const postShopOrderItem = async ({ data }) => {
    const config = {
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Token ${getUserToken()}`,
        },
    };
    const response = await axios.post(shopOrderItemCRUDUrl, data, config);
    return response;
};

export const patchShopOrderItem = async ({ data, id }) => {
    const config = {
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Token ${getUserToken()}`,
        },
    };
    const response = await axios.patch(shopOrderItemCRUDUrl + id + "/", data, config);
    return response;
};

export const postReturnCancellationLog = async (formData) => {
    const config = {
        headers: {
            "Authorization": `Token ${getUserToken()}`,
        },
    };
    const response = await axios.post(shopOrderReturnCancellationUrl, formData, config);
    return response;
};

export const deleteShopOrderItem = async ({ id }) => {
    const config = {
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Token ${getUserToken()}`,
        },
    };
    const response = await axios.delete(shopOrderItemCRUDUrl + id + "/", config);
    return response;
};